import React from 'react';
import { cn } from '@/utils/cn';
import { AlertCircle, CheckCircle, Info, XCircle } from 'lucide-react';

interface AlertProps {
  children: React.ReactNode;
  variant?: 'default' | 'destructive' | 'success' | 'warning';
  className?: string;
}

export function Alert({ children, variant = 'default', className }: AlertProps) {
  const icons = {
    default: <Info className="icon-md" />,
    destructive: <XCircle className="icon-md" />,
    success: <CheckCircle className="icon-md" />,
    warning: <AlertCircle className="icon-md" />
  };

  return (
    <div
      className={cn(
        'relative w-full rounded-lg border p-4',
        {
          'bg-background text-foreground': variant === 'default',
          'border-destructive/50 text-destructive dark:border-destructive': variant === 'destructive',
          'border-green-500/50 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950': variant === 'success',
          'border-yellow-500/50 text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950': variant === 'warning',
        },
        className
      )}
    >
      <div className="flex gap-2">
        {icons[variant]}
        <div className="flex-1 text-sm">{children}</div>
      </div>
    </div>
  );
}

interface AlertTitleProps {
  children: React.ReactNode;
  className?: string;
}

export function AlertTitle({ children, className }: AlertTitleProps) {
  return (
    <h5 className={cn('mb-1 font-medium leading-none tracking-tight', className)}>
      {children}
    </h5>
  );
}

interface AlertDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export function AlertDescription({ children, className }: AlertDescriptionProps) {
  return (
    <div className={cn('text-sm [&_p]:leading-relaxed', className)}>
      {children}
    </div>
  );
}